import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { fetchProgramReports, generateProgramReport, pollProgramReport } from '../../../actions/programReports'
import Loader from '../../common/loader/Loader'
import ProgramReportCard from './ProgramReportCard'

import classes from './ReportsTab.module.scss'

const downloadFile = (fileUrl) => {
  const link = document.createElement('a')
  link.href = fileUrl
  link.setAttribute('download', '')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ReportsTab = () => {
  const { t } = useTranslation()
  const program = useSelector((state) => state.programDashboard.program)
  const courseId = program?.id

  const [reports, setReports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  useEffect(() => {
    if (!courseId) { return undefined }

    let mounted = true
    setIsLoading(true)

    fetchProgramReports(courseId)
      .then((data) => { if (mounted) { setReports(data) } })
      .catch(() => {
        if (mounted) { toast.error(t('programs.manage.reports.loadError'), { position: toast.POSITION.TOP_RIGHT }) }
      })
      .finally(() => { if (mounted) { setIsLoading(false) } })

    return () => { mounted = false }
  }, [courseId, t])

  // Generating a report takes over the whole tab with a loading state (per design):
  // regenerate fresh, poll the job, then auto-download the file.
  const handleDownload = async (reportType) => {
    if (isDownloading) { return }

    setIsDownloading(true)

    try {
      const jobId = await generateProgramReport(courseId, reportType)
      const fileUrl = await pollProgramReport(jobId)
      if (isMountedRef.current) { downloadFile(fileUrl) }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(t('programs.manage.reports.generateError'), { position: toast.POSITION.TOP_RIGHT })
      }
    } finally {
      if (isMountedRef.current) { setIsDownloading(false) }
    }
  }

  if (isLoading || isDownloading) {
    return (
      <div className={classes.loaderWrapper}>
        <Loader>{isDownloading ? t('programs.manage.reports.downloadingReport') : t('programs.manage.reports.loadingReports')}</Loader>
      </div>
    )
  }

  return (
    <section className={classes.reportsTab}>
      {reports.map((report) => (
        <ProgramReportCard
          key={report.reportType}
          reportType={report.reportType}
          title={report.title}
          description={report.description}
          onDownload={handleDownload}
        />
      ))}
    </section>
  )
}

export default ReportsTab
